import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

const categoryIcons: Record<string, string> = {
  Shoes: "ri-footprint-line",
  "Hoodies/Sweaters": "ri-t-shirt-line",
  "T-Shirts": "ri-t-shirt-air-line",
  Jackets: "ri-shirt-line",
  "Pants/Shorts": "ri-pantone-line",
  Headwear: "ri-base-station-line",
  Sets: "ri-shapes-line",
  "Underwear/Underpants": "ri-checkbox-blank-circle-line",
  Jersey: "ri-shirt-line",
  Accessories: "ri-vip-crown-line",
  Bags: "ri-shopping-bag-3-line",
  Belts: "ri-link",
  Perfumes: "ri-drop-line",
  "Other Stuff": "ri-more-2-line",
};

export default function Navbar() {
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const closeDropdown = useCallback(() => {
    setShowDropdown(false);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeDropdown]);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileCategoryOpen(false);
    setMobileResourcesOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-grid-fill text-background-50 text-base md:text-lg"></i>
            </div>
            <span className="font-heading text-base md:text-lg font-bold text-foreground-950 whitespace-nowrap">
              Hipobuy Spreadsheet
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              Home
            </Link>
            <Link to="/hipobuy-spreadsheet-guide" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              Guides
            </Link>
            <Link to="/hipobuy-spreadsheet-tutorial" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              Tutorial
            </Link>
            <Link to="/hipobuy-spreadsheet-safety" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              Safety
            </Link>
            <Link to="/hipobuy-spreadsheet-alternatives" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              Alternatives
            </Link>

            {/* Categories Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${showDropdown ? "rotate-180" : ""}`}></i>
              </button>

              {showDropdown && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-lg border border-background-200/70 py-2 shadow-lg z-50 max-h-[400px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={closeDropdown}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors cursor-pointer"
                    >
                      <i className={`${categoryIcons[cat.name] || "ri-price-tag-3-line"} text-base text-primary-400`}></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="ml-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Explore Resources
              <i className="ri-arrow-right-line ml-1.5 text-xs"></i>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {/* Mobile Menu - Enhanced */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[calc(100vh-64px)] overflow-y-auto">
            <div className="flex flex-col py-2">
              {/* Mobile CTA */}
              <div className="px-4 py-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobileMenu}
                  className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  hipobuy sheet — Shop Now
                  <i className="ri-arrow-right-up-line text-base"></i>
                </a>
              </div>

              <div className="border-t border-background-200/50 my-1"></div>

              {/* Main Pages */}
              <Link to="/" onClick={closeMobileMenu} className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg cursor-pointer">
                <i className="ri-home-4-line text-lg text-primary-500"></i>
                Home
              </Link>

              {/* Resources Accordion */}
              <div>
                <button
                  onClick={() => setMobileResourcesOpen(!mobileResourcesOpen)}
                  className="flex items-center justify-between w-full px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg cursor-pointer"
                >
                  <span className="flex items-center gap-3">
                    <i className="ri-book-open-line text-lg text-primary-500"></i>
                    Resources & Guides
                  </span>
                  <i className={`ri-arrow-down-s-line text-base text-foreground-500 transition-transform duration-200 ${mobileResourcesOpen ? "rotate-180" : ""}`}></i>
                </button>
                {mobileResourcesOpen && (
                  <div className="bg-background-100/50 mx-3 rounded-lg mb-1">
                    <Link to="/hipobuy-spreadsheet-guide" onClick={closeMobileMenu} className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-500 cursor-pointer">Complete Guide 2026</Link>
                    <Link to="/hipobuy-spreadsheet-tutorial" onClick={closeMobileMenu} className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-500 cursor-pointer">Step-by-Step Tutorial</Link>
                    <Link to="/hipobuy-spreadsheet-review" onClick={closeMobileMenu} className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-500 cursor-pointer">Honest Review</Link>
                    <Link to="/hipobuy-spreadsheet-beginner-mistakes" onClick={closeMobileMenu} className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-500 cursor-pointer">Beginner Mistakes</Link>
                    <Link to="/best-spreadsheet-resources" onClick={closeMobileMenu} className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-500 cursor-pointer">Best Resources</Link>
                  </div>
                )}
              </div>

              <Link to="/hipobuy-spreadsheet-safety" onClick={closeMobileMenu} className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg cursor-pointer">
                <i className="ri-shield-check-line text-lg text-accent-500"></i>
                Safety & Trust
              </Link>

              <Link to="/hipobuy-spreadsheet-alternatives" onClick={closeMobileMenu} className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg cursor-pointer">
                <i className="ri-scales-3-line text-lg text-secondary-500"></i>
                Alternatives
              </Link>

              <Link to="/hipobuy-spreadsheet-categories" onClick={closeMobileMenu} className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg cursor-pointer">
                <i className="ri-grid-line text-lg text-secondary-500"></i>
                Category Guide
              </Link>

              <div className="border-t border-background-200/50 my-1"></div>

              {/* Mobile Categories Accordion - Enhanced */}
              <div>
                <button
                  onClick={() => setMobileCategoryOpen(!mobileCategoryOpen)}
                  className="flex items-center justify-between w-full px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg cursor-pointer"
                >
                  <span className="flex items-center gap-3">
                    <i className="ri-store-2-line text-lg text-primary-500"></i>
                    Shop Categories
                    <span className="px-2 py-0.5 bg-primary-100 text-primary-600 text-xs rounded-full font-medium">14</span>
                  </span>
                  <i className={`ri-arrow-down-s-line text-base text-foreground-500 transition-transform duration-200 ${mobileCategoryOpen ? "rotate-180" : ""}`}></i>
                </button>
                {mobileCategoryOpen && (
                  <div className="grid grid-cols-2 gap-1 px-3 mb-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={closeMobileMenu}
                        className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-50 hover:text-primary-500 rounded-lg transition-colors cursor-pointer bg-background-100/50"
                      >
                        <i className={`${categoryIcons[cat.name] || "ri-price-tag-3-line"} text-sm text-primary-400`}></i>
                        <span className="truncate">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Comparisons */}
              <div className="border-t border-background-200/50 my-1"></div>
              <div className="px-4 py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Comparisons</div>
              <Link to="/kakobuy-spreadsheet" onClick={closeMobileMenu} className="px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg cursor-pointer">Kakobuy Spreadsheet</Link>
              <Link to="/cssbuy-spreadsheet" onClick={closeMobileMenu} className="px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg cursor-pointer">CSSBuy Spreadsheet</Link>
              <Link to="/superbuy-spreadsheet" onClick={closeMobileMenu} className="px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg cursor-pointer">Superbuy Spreadsheet</Link>
              <Link to="/oopbuy-spreadsheet" onClick={closeMobileMenu} className="px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg cursor-pointer">Oopbuy Spreadsheet</Link>

              {/* Bottom CTA */}
              <div className="border-t border-background-200/50 mt-2"></div>
              <div className="px-4 py-4">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobileMenu}
                  className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-foreground-950 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-colors cursor-pointer"
                >
                  buy it now
                  <i className="ri-arrow-right-up-line text-base"></i>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}